import { api_portalOfficeHospitalListing_medicalSubject } from '@/init/swagger';
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  Stack,
  Typography,
} from '@mui/material';
import { MuiChip } from '@/components/chip/MuiChip';
import { PhoneIcon } from '@/features/icon/PhoneIcon';
import { useLogic } from './logic';
import { HospitalListingOutput } from '@/helpers/parseHospitalListings';
import { MedicalCareIcon } from '@/features/icon/MedicalCareIcon';
import { HpUrlIcon } from '@/features/icon/HpUrlIcon';
import { MuiButton } from '@/components/button/MuiButton';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { CanRequestLabel } from '@/features/label/CanRequestLabel';
import { PartnershipsSummary } from '@/features/partnership/components/PartnershipsSummary';

type Props = {
  listing: HospitalListingOutput;
  hospitalMedicalSubjects: api_portalOfficeHospitalListing_medicalSubject[];
};

export function PublicHomeHospitalListingCard({
  listing,
  hospitalMedicalSubjects,
}: Props) {
  const {
    cardHref,
    thumbnailInfo,
    portalOfficeInfo,
    onClickRequest,
    isCarelogood,
  } = useLogic({
    hospitalMedicalSubjects,
    listing,
  });

  return (
    <CardActionArea href={cardHref} target="_blank">
      <Card
        sx={{
          display: 'flex',
          flexDirection: {
            xs: 'column',
            md: 'row',
          },
          boxShadow: `0px 0px 2px 0px rgba(0, 0, 0, 0.10), 0px 4px 8px 0px rgba(0, 0, 0, 0.10)`,
          borderRadius: 2,
          padding: 2,
          gap: {
            xs: 2,
            md: 3,
          },
          backgroundColor: 'white',
          position: 'relative',
          alignItems: 'flex-start',
          '&:hover': {
            '& .highlight p': {
              textDecoration: 'underline',
              textDecorationColor: '#3ec9be',
              color: '#3ec9be',
            },
            '& .MuiCardMedia-img': {
              border: 'solid 2px #3ec9be',
            },
          },
        }}
      >
        <Box
          sx={{
            width: {
              xs: '100%',
              md: 320,
            },
            maxWidth: {
              xs: '100%',
              md: '40%',
            },
            aspectRatio: {
              xs: '1 / 1',
              md: '16 / 9',
            },
            height: {
              xs: 263,
              md: 220,
            },
            borderRadius: 1,
            margin: {
              xs: 'auto',
              md: '16px auto',
            },
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <img
            src={thumbnailInfo.image}
            alt={thumbnailInfo.alt}
            style={{
              width: '100%',
              height: '100%',
              borderRadius: 'inherit',
              objectFit: 'cover',
            }}
          />
        </Box>
        <CardContent
          sx={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            p: 0,
            py: 1,
            gap: 3,
            ':last-child': { pb: 1 },
            flex: 1,
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  gap: 0.5,
                }}
              >
                <Box>
                  <MuiChip
                    label={`医科`}
                    size="small"
                    sx={{
                      fontWeight: 700,
                      backgroundColor: '#FFAC8A',
                      height: '24px',
                      fontSize: '14px',
                      px: 0.5,
                    }}
                  />
                </Box>
                <div className="highlight">
                  <Typography
                    sx={{
                      verticalAlign: 'sub',
                      fontSize: '20px',
                      fontWeight: 700,
                    }}
                  >
                    {portalOfficeInfo.name}
                  </Typography>
                </div>
              </Box>
            </Box>

            {isCarelogood && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                  gap: 1,
                }}
              >
                <CanRequestLabel />
              </Box>
            )}

            <PartnershipsSummary portalOfficeId={listing.portal_office.id} />

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <Typography
                color="#595960"
                variant="JP/Body/m/Regular/Normal"
                sx={{
                  order: {
                    xs: 1,
                    md: 2,
                  },
                }}
              >
                {portalOfficeInfo.postCode
                  ? `${portalOfficeInfo.postCode} ${portalOfficeInfo.address}`
                  : `${portalOfficeInfo.address}`}
              </Typography>
            </Box>

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <PhoneIcon color="default" width={16} height={16} />
              <Typography
                color="#595960"
                variant="JP/Body/m/Regular/Normal"
                sx={{
                  order: {
                    xs: 1,
                    md: 2,
                  },
                }}
              >
                {portalOfficeInfo.tel}
              </Typography>
            </Box>

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <MedicalCareIcon color="default" width={18} height={18} />
              <Typography
                color="#595960"
                variant="JP/Body/m/Regular/Normal"
                sx={{
                  order: {
                    xs: 1,
                    md: 2,
                  },
                }}
              >
                {portalOfficeInfo.medicalSubjects}
              </Typography>
            </Box>

            {portalOfficeInfo.hpUrl && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <HpUrlIcon
                  color="default"
                  width={18}
                  height={18}
                  sxProps={{
                    marginTop: '2px',
                  }}
                />
                <Typography
                  color="#595960"
                  variant="JP/Body/m/Regular/Normal"
                  sx={{
                    order: {
                      xs: 1,
                      md: 2,
                    },
                  }}
                >
                  {portalOfficeInfo.hpUrl}
                </Typography>
              </Box>
            )}

            {isCarelogood && (
              <Stack
                direction="row"
                alignItems="center"
                sx={{
                  color: 'primary.main',
                  justifyContent: {
                    xs: 'center',
                    md: 'flex-start',
                  },
                  my: 1,
                }}
              >
                <MuiButton variant="text" onClick={onClickRequest}>
                  <Typography variant="JP/Body/s/Medium/None">
                    医療機関お問い合わせへ
                  </Typography>
                  <NavigateNextIcon fontSize="small" />
                </MuiButton>
              </Stack>
            )}

            <Box
              sx={{
                marginTop: 1,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <Typography variant="JP/Body/m/Medium/Tight" color="#C4C4C4">
                更新日：{portalOfficeInfo.updatedAt}
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </CardActionArea>
  );
}
